import React, { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "../api/axiosConfig";
import CourseCard from "../components/CourseCard";
import { getToken } from "../api/auth";
import { usePurchases } from "../api/usePurchases";
import { blogImageSrc, COURSE_PLACEHOLDER } from "../api/blogImage";

const TYPE_FILTERS = [
  { value: "", label: "All" },
  { value: "full-length", label: "Full Length" },
  { value: "subject-wise", label: "Subject Wise" },
  { value: "topic-wise", label: "Topic Wise" },
];

const typeLabel = {
  "full-length": "Full Length",
  "subject-wise": "Subject Wise",
  "topic-wise": "Topic Wise",
};

const PAGE_SIZE = 9;

function pageItems(current, total) {
  if (total <= 7) {
    return Array.from({ length: total }, (_, index) => index + 1);
  }

  const items = [1];
  const start = Math.max(2, current - 1);
  const end = Math.min(total - 1, current + 1);

  if (start > 2) items.push("ellipsis-start");
  for (let page = start; page <= end; page += 1) items.push(page);
  if (end < total - 1) items.push("ellipsis-end");
  items.push(total);
  return items;
}

function Courses() {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [searchParams, setSearchParams] = useSearchParams();
  const categorySlug = searchParams.get("category") || "";
  const courseType = searchParams.get("type") || "";
  const requestedPage = Math.max(parseInt(searchParams.get("page") || "1", 10) || 1, 1);

  useEffect(() => {
    let cancelled = false;

    async function fetchCourses() {
      try {
        setLoading(true);
        const response = await api.get("/courses", {
          params: {
            page: requestedPage,
            limit: PAGE_SIZE,
            ...(courseType ? { course_type: courseType } : {}),
            ...(categorySlug ? { category: categorySlug } : {}),
          },
        });
        if (cancelled) return;
        const all = response.data.courses || [];
        if (typeof response.data.totalPages === "number") {
          setCourses(all);
          setPage(response.data.page || requestedPage);
          setTotalPages(response.data.totalPages || 0);
        } else {
          const pages = Math.ceil(all.length / PAGE_SIZE);
          const current = pages === 0 ? 1 : Math.min(requestedPage, pages);
          const start = (current - 1) * PAGE_SIZE;
          setCourses(all.slice(start, start + PAGE_SIZE));
          setPage(current);
          setTotalPages(pages);
        }
        setError(null);
      } catch (err) {
        console.error("Error fetching courses:", err);
        if (!cancelled) {
          setError("Failed to load courses. Please try again later.");
          setCourses([]);
          setTotalPages(0);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    fetchCourses();
    window.scrollTo({ top: 0, behavior: "smooth" });
    return () => {
      cancelled = true;
    };
  }, [courseType, categorySlug, requestedPage]);

  const setType = (value) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set("type", value);
    else next.delete("type");
    next.delete("page");
    setSearchParams(next);
  };

  const goToPage = (nextPage) => {
    const next = Math.min(Math.max(nextPage, 1), totalPages || 1);
    if (next === requestedPage) return;
    const params = new URLSearchParams(searchParams);
    if (next <= 1) params.delete("page");
    else params.set("page", String(next));
    setSearchParams(params);
  };

  const { loggedIn, loading: purchasesLoading, isPurchased } = usePurchases();
  const enrollTo = getToken() ? "/dashboard" : "/register";

  return (
    <main>
      <div className="breadcrumb-area">
        <div className="container">
          <div className="row">
            <div className="col-12">
              <nav aria-label="breadcrumb">
                <ol className="breadcrumb">
                  <li className="breadcrumb-item">
                    <Link to="/">Home</Link>
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">
                    Courses
                  </li>
                </ol>
              </nav>
            </div>
          </div>
        </div>
      </div>

      <section className="section-space">
        <div className="container">
          <div className="row mb-4">
            <div className="col-lg-8 mx-auto text-center">
              <h1 className="mb-3">
                Explore Our <span className="text-gradient">Courses</span>
              </h1>
              <p>
                Choose from our wide variety of professional and beginner-friendly courses
              </p>
            </div>
          </div>

          <div className="course-filters" role="group" aria-label="Filter by course type">
            {TYPE_FILTERS.map((filter) => (
              <button
                key={filter.label}
                type="button"
                className={courseType === filter.value ? "active" : undefined}
                onClick={() => setType(filter.value)}
              >
                {filter.label}
              </button>
            ))}
          </div>

          {loading && (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          )}

          {error && (
            <div className="alert alert-danger" role="alert">
              {error}
            </div>
          )}

          {!loading && courses.length > 0 ? (
            <div className="row g-4 courses-grid">
              {courses.map((course) => {
                const tax = Number(course.tax_percentage);
                const purchased = loggedIn && isPurchased(course.id);
                const checking = loggedIn && purchasesLoading;

                return (
                  <div key={course._id} className="col-lg-4 col-md-6 col-sm-12 d-flex">
                    <CourseCard
                      catalog
                      title={course.title}
                      thumbnail={blogImageSrc(course.banner, COURSE_PLACEHOLDER)}
                      instructor={course.category?.title}
                      description={course.description}
                      badge={typeLabel[course.course_type] || course.course_type}
                      includes={course.includes}
                      includeLimit={2}
                      price={course.priceLabel || "Free"}
                      taxNote={tax > 0 ? `+ ${tax}% tax` : ""}
                      brochure={
                        course.brochure ? blogImageSrc(course.brochure) : ""
                      }
                      to={purchased ? `/my-courses/${course.id}` : enrollTo}
                      actionLabel={checking ? "Checking..." : purchased ? "View course" : "Enroll Now"}
                      actionClassName={purchased ? "btn-view-course" : ""}
                      actionPending={checking}
                      detailTo={`/courses/${course.id}`}
                    />
                  </div>
                );
              })}
            </div>
          ) : null}

          {!loading && !error && totalPages > 0 ? (
            <nav className="blog-pagination" aria-label="Course pages">
              <button type="button" onClick={() => goToPage(page - 1)} disabled={page <= 1}>
                Previous
              </button>
              {pageItems(page, totalPages).map((item) =>
                typeof item === "string" ? (
                  <span key={item} className="page-ellipsis">
                    …
                  </span>
                ) : (
                  <button
                    key={item}
                    type="button"
                    className={item === page ? "active" : undefined}
                    onClick={() => goToPage(item)}
                    aria-current={item === page ? "page" : undefined}
                  >
                    {item}
                  </button>
                )
              )}
              <button
                type="button"
                onClick={() => goToPage(page + 1)}
                disabled={page >= totalPages}
              >
                Next
              </button>
            </nav>
          ) : null}

          {!loading && !error && courses.length === 0 && (
            <div className="alert alert-info text-center" role="alert">
              No courses available at the moment. Please check back soon!
            </div>
          )}
        </div>
      </section>
    </main>
  );
}

export default Courses;
